import { FC, useCallback, useMemo, useState } from "react";
import {
  Button,
  Label,
  LoadingButton,
  RadioGroup,
  RadioGroupItem,
  Stack,
  TagsInput,
  TagsInputClear,
  TagsInputInput,
  TagsInputItem,
  TagsInputLabel,
  TagsInputList,
  toast,
} from "@altimateai/lego";
import { IconBuilding, IconMail, IconRefresh, IconUsers } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { sendSessionToEmail } from "@/helpers/apis";
import { cn } from "@/lib/utils";
import ShareTeamCombobox from "./ShareTeamCombobox";

interface ShareEmailTabProps {
  sessionId: string;
  onSuccess: () => void;
}

export enum EmailDestinationType {
  SpecificEmails = "specific_emails",
  AllAltimateUsers = "all_altimate_users",
  Team = "team",
}

const EMAIL_DESTINATION_OPTIONS: {
  value: EmailDestinationType;
  icon: typeof IconMail;
  label: string;
  description: string;
  disabled?: boolean;
}[] = [
  {
    value: EmailDestinationType.SpecificEmails,
    icon: IconMail,
    label: "Specific email addresses",
    description: "Enter individual recipient emails",
  },
  {
    value: EmailDestinationType.AllAltimateUsers,
    icon: IconBuilding,
    label: "Anyone in this organization",
    description: "Email all signed-in members of this tenant",
  },
  {
    value: EmailDestinationType.Team,
    icon: IconUsers,
    label: "Team",
    description: "Email all members of selected teams",
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email.trim());

/**
 * Email tab for ShareDialog. Sends the current Studio session as an
 * email to one of three destinations via `studio/session_to_email_mailing_list`:
 * - specific_emails: explicit recipient addresses
 * - all_altimate_users: every active tenant user (resolved server-side)
 * - team: members of the selected teams (resolved server-side)
 */
const ShareEmailTab: FC<ShareEmailTabProps> = ({ sessionId, onSuccess }) => {
  const [destinationType, setDestinationType] = useState<EmailDestinationType>(
    EmailDestinationType.SpecificEmails
  );
  const [emails, setEmails] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  const handleValidateEmail = useCallback((email: string): boolean => validateEmail(email), []);

  const validEmails = emails.filter(validateEmail);
  const hasInvalidEmails = emails.some(email => !validateEmail(email));

  const { mutate: sendEmail, isPending: isSending } = useMutation({
    mutationFn: () => {
      if (destinationType === EmailDestinationType.SpecificEmails) {
        return sendSessionToEmail({
          session_id: sessionId,
          recipient_type: "specific_emails",
          emails: validEmails,
        });
      }
      if (destinationType === EmailDestinationType.Team) {
        return sendSessionToEmail({
          session_id: sessionId,
          recipient_type: "team",
          team_ids: selectedTeamIds.map(Number),
        });
      }
      return sendSessionToEmail({
        session_id: sessionId,
        recipient_type: "all_altimate_users",
      });
    },
    onSuccess: () => {
      toast({ title: "Email sent", description: "Your session report is on its way." });
      setEmails([]);
      setSelectedTeamIds([]);
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send email",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const canSend =
    destinationType === EmailDestinationType.AllAltimateUsers ||
    (destinationType === EmailDestinationType.Team && selectedTeamIds.length > 0) ||
    (destinationType === EmailDestinationType.SpecificEmails &&
      validEmails.length > 0 &&
      !hasInvalidEmails);

  const sendButtonLabel = useMemo(() => {
    if (destinationType === EmailDestinationType.AllAltimateUsers) return "Send to organization";
    if (destinationType === EmailDestinationType.Team) {
      const n = selectedTeamIds.length;
      return `Send to ${n} team${n === 1 ? "" : "s"}`;
    }
    const n = validEmails.length;
    return `Send to ${n} recipient${n === 1 ? "" : "s"}`;
  }, [destinationType, selectedTeamIds.length, validEmails.length]);

  const handleSend = () => {
    if (!canSend) return;
    sendEmail();
  };

  // The radios are rendered from EMAIL_DESTINATION_OPTIONS, so the
  // incoming value is always a valid EmailDestinationType.
  const handleDestinationTypeChange = (v: string) => {
    const next = v as EmailDestinationType;
    setDestinationType(next);
    if (next !== EmailDestinationType.SpecificEmails) setEmails([]);
    if (next !== EmailDestinationType.Team) setSelectedTeamIds([]);
  };

  return (
    <div className="al-space-y-4 al-py-4">
      {/* Destination type selector */}
      <div className="al-space-y-3">
        <Label>Send to</Label>
        <RadioGroup
          value={destinationType}
          onValueChange={handleDestinationTypeChange}
          className="al-space-y-2"
        >
          {EMAIL_DESTINATION_OPTIONS.map(opt => {
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                htmlFor={`email-dest-${opt.value}`}
                className={cn(
                  "al-flex al-items-center al-space-x-3 al-p-3 al-rounded-md al-border",
                  opt.disabled
                    ? "al-cursor-not-allowed al-opacity-50"
                    : "al-cursor-pointer hover:al-bg-muted/50"
                )}
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`email-dest-${opt.value}`}
                  disabled={opt.disabled}
                />
                <Icon className="al-h-5 al-w-5 al-text-muted-foreground" />
                <div className="al-flex-1">
                  <span className="al-font-medium">{opt.label}</span>
                  <p className="al-text-sm al-text-muted-foreground">{opt.description}</p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Specific email addresses input */}
      {destinationType === EmailDestinationType.SpecificEmails && (
        <TagsInput
          value={emails}
          onValueChange={setEmails}
          onValidate={handleValidateEmail}
          addOnPaste
          addOnTab
          // Commit whatever the user typed when focus leaves the field
          // (e.g. when they click "Send"). Without this, a user who types
          // one email and clicks Send sees "Send to 0 recipients" because
          // no delimiter was ever pressed and the tag is never created.
          blurBehavior="add"
          delimiter=","
          className="al-w-full"
          disabled={isSending}
        >
          <TagsInputLabel>Email Recipients</TagsInputLabel>
          <TagsInputList>
            {emails.map(email => (
              <TagsInputItem
                key={email}
                value={email}
                className={
                  validateEmail(email)
                    ? "al-bg-blue-100 al-text-blue-800 al-border-blue-200"
                    : "al-bg-red-100 al-text-red-800 al-border-red-200"
                }
              >
                {email}
              </TagsInputItem>
            ))}
            <TagsInputInput
              placeholder={
                emails.length === 0 ? "Type an email and press Enter, Tab, or comma" : ""
              }
              disabled={isSending}
              aria-label="add emails"
            />
          </TagsInputList>
          <TagsInputClear asChild>
            <Button variant="ghost" size="sm" disabled={isSending} aria-label="clear emails">
              <IconRefresh className="al-h-4 al-w-4" />
            </Button>
          </TagsInputClear>
        </TagsInput>
      )}

      {hasInvalidEmails && destinationType === EmailDestinationType.SpecificEmails && (
        <p className="al-text-sm al-text-red-600">One or more email addresses are invalid.</p>
      )}

      {/* Team picker */}
      {destinationType === EmailDestinationType.Team && (
        <ShareTeamCombobox selectedTeamIds={selectedTeamIds} onChange={setSelectedTeamIds} />
      )}

      <Stack direction="row" justify="end">
        <LoadingButton loading={isSending} disabled={!canSend} onClick={handleSend}>
          <IconMail className="al-h-4 al-w-4 al-mr-2" />
          {sendButtonLabel}
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default ShareEmailTab;
